import { useMemo, useState } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { LabHospitalComparisonRow } from "@/api/intelligence";
import { formatCount, formatHours, formatInr } from "@/lib/intelligenceFormat";
import { ArrowUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

type Props = { rows: LabHospitalComparisonRow[] };
type SortKey = keyof LabHospitalComparisonRow;

export function LabHospitalComparisonTable({ rows }: Props) {
	const [sortKey, setSortKey] = useState<SortKey>("revenue");
	const [sortAsc, setSortAsc] = useState(false);

	const sorted = useMemo(() => {
		const copy = [...rows];
		copy.sort((a, b) => {
			const av = a[sortKey];
			const bv = b[sortKey];
			if (av == null && bv == null) return 0;
			if (av == null) return 1;
			if (bv == null) return -1;
			if (typeof av === "string" && typeof bv === "string") {
				return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
			}
			return sortAsc ? Number(av) - Number(bv) : Number(bv) - Number(av);
		});
		return copy;
	}, [rows, sortKey, sortAsc]);

	if (!rows.length) return null;

	const toggleSort = (key: SortKey) => {
		if (sortKey === key) setSortAsc(!sortAsc);
		else {
			setSortKey(key);
			setSortAsc(false);
		}
	};

	const SortHead = ({ label, field }: { label: string; field: SortKey }) => (
		<TableHead
			className={cn(
				"cursor-pointer select-none whitespace-nowrap",
				field !== "hospitalName" && "text-right"
			)}
			onClick={() => toggleSort(field)}
		>
			<span className="inline-flex items-center gap-1">
				{label}
				<ArrowUpDown className="h-3 w-3 opacity-50" />
			</span>
		</TableHead>
	);

	return (
		<Card>
			<CardHeader className="pb-2">
				<CardTitle className="text-base font-medium">Hospital comparison</CardTitle>
			</CardHeader>
			<CardContent className="overflow-x-auto">
				<Table>
					<TableHeader>
						<TableRow>
							<SortHead label="Hospital" field="hospitalName" />
							<SortHead label="Revenue" field="revenue" />
							<SortHead label="Tests" field="testVolume" />
							<SortHead label="Pending" field="pendingTests" />
							<SortHead label="Avg TAT" field="averageTatHours" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{sorted.map((row) => (
							<TableRow key={row.hospitalId}>
								<TableCell className="font-medium">{row.hospitalName}</TableCell>
								<TableCell className="text-right tabular-nums">{formatInr(row.revenue)}</TableCell>
								<TableCell className="text-right tabular-nums">{formatCount(row.testVolume)}</TableCell>
								<TableCell className="text-right tabular-nums">{formatCount(row.pendingTests)}</TableCell>
								<TableCell className="text-right tabular-nums">
									{row.averageTatHours != null ? formatHours(row.averageTatHours) : "—"}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</CardContent>
		</Card>
	);
}
